<template>
<div class="address-fields-component mb-2">
  <div class="address-fields-component__basic-info">
    <ValidationProvider rules="required"
                        name="Город"
                        :custom-messages="{ required: 'Необходимо ввести город' }"
                        slim
    >
      <template slot-scope="{ errors }">
        <CustomInput label="Город *"
                     v-model="form.city"
                     :error="errors[0]"
                     class="address-fields-component__city"
                     type="text"
                     placeholder="Город"
        />
      </template>
    </ValidationProvider>
    <ValidationProvider rules="required"
                        name="Улица"
                        :custom-messages="{ required: 'Необходимо ввести улицу' }"
                        slim
    >
      <template slot-scope="{ errors }">
        <CustomInput label="Улица *"
                     v-model="form.street"
                     :error="errors[0]"
                     class="address-fields-component__street"
                     type="text"
                     placeholder="Улица"
        />
      </template>
    </ValidationProvider>
    <ValidationProvider rules="required"
                        name="Дом"
                        :custom-messages="{ required: 'Необходимо ввести дом' }"
                        slim
    >
      <template slot-scope="{ errors }">
        <CustomInput label="Дом *"
                     v-model="form.house"
                     :error="errors[0]"
                     class="address-fields-component__house"
                     type="text"
                     placeholder="Номер"
                     maxlength="10"
        />
      </template>
    </ValidationProvider>
    <CustomInput class="address-fields-component__house"
                 label="Корпус"
                 v-model="form.block"
                 type="text"
                 placeholder="Корпус"
    />
    <div v-if="collapsible" class="order-delivery__expand-button-wrapper">
      <label class="form-control-label invisible">.</label>
      <button @click="toggleDetailsCollapse"
              :aria-expanded="`${detailsCollapsed ? 'false' : 'true'}`"
              class="order-delivery__expand-button"
              type="button"
      >
        <IconArrowDown />
      </button>
    </div>
  </div>

  <TransitionExpand v-if="collapsible">
    <div v-show="!detailsCollapsed" class="address-fields-component__detail-info">
      <CustomInput class="address-fields-component__apt"
                   label="Квартира"
                   v-model="form.apartment"
                   inputmode="numeric"
                   placeholder="Номер"
      />
      <CustomInput class="address-fields-component__entrance"
                   label="Подъезд"
                   v-model="form.entrance"
                   inputmode="numeric"
                   placeholder="Номер"
      />
      <CustomInput class="address-fields-component__floor"
                   label="Этаж"
                   v-model="form.floor"
                   type="number"
                   inputmode="numeric"
                   placeholder="Номер"
      />
      <CustomSelect label="Домофон"
                    :options="hasIntercomOptions"
                    v-model="form.has_intercom"
                    :reduce="o => o.value"
                    class="address-fields-component__intercom"
                    :getOptionKey="o => o.value"
                    :clearable="false"
                    :searchable="false"
      />
    </div>
  </TransitionExpand>
  <div v-else class="address-fields-component__detail-info">
    <CustomInput class="address-fields-component__apt"
                 label="Квартира"
                 v-model="form.apartment"
                 inputmode="numeric"
                 placeholder="Номер"
    />
    <CustomInput class="address-fields-component__entrance"
                 label="Подъезд"
                 v-model="form.entrance"
                 inputmode="numeric"
                 placeholder="Номер"
    />
    <CustomInput class="address-fields-component__floor"
                 label="Этаж"
                 v-model="form.floor"
                 type="number"
                 inputmode="numeric"
                 placeholder="Номер"
    />
    <CustomSelect label="Домофон"
                  :options="hasIntercomOptions"
                  v-model="form.has_intercom"
                  :reduce="o => o.value"
                  class="address-fields-component__intercom"
                  :getOptionKey="o => o.value"
                  :clearable="false"
                  :searchable="false"
    />
  </div>

  <address-hint v-if="!editing" class="mt-2" />
</div>
</template>

<script>
import { ValidationProvider } from '@/helpers/veeValidateCustomized'
import CustomField from '@/components/inputs/CustomField'
import CustomInput from '@/components/inputs/CustomInput'
import CustomSelect from '@/components/inputs/CustomSelect'
import TransitionExpand from '@/components/AsyncTransitionExpand'
import AddressHint from '@/components/addresses/AddressHint'
import { mapGetters } from 'vuex'

export default {
  props: {
    collapsible: {
      required: false,
      type: Boolean,
      default: true,
    },

    address: {
      required: false,
      type: Object,
      default: () => null,
    },

    editing: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  components: {
    ValidationProvider,
    TransitionExpand,
    CustomField,
    CustomInput,
    CustomSelect,
    IconArrowDown: () => import('@/assets/images/icons/icon-arrow-down-small.svg?inline'),
    AddressHint,
  },

  created() {
    if (this.address) {
      this.assignFormValues()
    } else if (this.activeCity && this.activeCity.name) {
      this.form.city = this.activeCity.name
    }

    this.detailsCollapsed = this.deliverySettings.extra_address_fields_state === 'collapsed'
  },

  computed: {
    ...mapGetters({
      activeCity: 'store/activeCity',
      deliverySettings: 'settings/delivery',
    }),

    hasIntercomOptions() {
      return [
        { value: true, label: 'Работает' },
        { value: false, label: 'Не работает' },
      ];
    },
  },

  methods: {
    assignFormValues() {
      this.form.city = this.address.city || ''
      this.form.street = this.address.street || ''
      this.form.house = this.address.house || ''
      this.form.block = this.address.block || ''

      this.form.apartment = this.address.apartment || ''
      this.form.entrance = this.address.entrance || ''
      this.form.floor = this.address.floor || ''
      this.form.has_intercom = this.address.has_intercom || false
      this.form.intercom_code = this.address.intercom_code || ''
      this.form.notes = this.address.notes || ''
    },

    toggleDetailsCollapse() {
      this.detailsCollapsed = !this.detailsCollapsed
    },

    getForm() {
      return {
        city: this.form.city,
        street: this.form.street,
        house: this.form.house,
        block: this.form.block,
        apartment: this.form.apartment,
        entrance: this.form.entrance,
        floor: this.form.floor,
        has_intercom: this.form.has_intercom,
        intercom_code: this.form.intercom_code,
        notes: this.form.notes,
      }
    },
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.$emit('input', this.getForm())
      },
    }
  },

  data() {
    return {
      detailsCollapsed: true,
      form: {
        city: '',
        street: '',
        house: '',
        block: '',
        apartment: '',
        entrance: '',
        floor: '',
        has_intercom: false,
        intercom_code: '',
        notes: '',
      }
    }
  }
}
</script>
